import { COVERAGE_ORDER } from "@platform/types";
import {
  COVERAGE_FORM,
  COVERAGE_NAMES,
  RFP_ACTION_VALUES,
} from "@platform/rfp-core";

import { getCoveragesDocuments } from "../RFP/coverages/utils";
import { DOCUMENT_ORDER, productDisplayNameMap } from "../../constants/rfp";

import { DUE_DATE_ALERT_STATUS } from "./constants";

const { DENTAL, DENTAL_PPO, DENTAL_DHMO, STD } = COVERAGE_NAMES;
const {
  PLAN_TYPE,
  DUAL_PLAN_TYPE,
  PLANS,
  DHMO,
  SINGLE_OPTION_PPO,
  DUAL_OPTION_PPO,
  DUAL_OPTION_PPO_1,
  DUAL_OPTION_PPO_2,
  PPO1_PPO2,
  NO_PLAN,
  DUAL_OPTION,
  PPO_DHMO,
  FICA_OPTION,
  FICA_BILLABLE,
  FICA_MATCHING_YES,
  FICA_MATCHING_NO,
  BILLABLE,
  NOT_BILLABLE,
} = COVERAGE_FORM;

// Pre-PB-712 STD drafts stored FICA as a checkbox: the boolean lived in
// ficaOption, and when checked the Billable/Not Billable choice overwrote the
// same key. Map those legacy shapes onto the new ficaOption (Yes/No/Unknown) +
// ficaBillable pair so resumed drafts render the new radios pre-selected.
// New drafts store Yes/No/Unknown (or null when unanswered) and pass through.
const LEGACY_FICA_PLAN_VALUES = {
  false: { [FICA_OPTION]: FICA_MATCHING_NO, [FICA_BILLABLE]: null },
  true: { [FICA_OPTION]: FICA_MATCHING_YES, [FICA_BILLABLE]: NOT_BILLABLE },
  [BILLABLE]: { [FICA_OPTION]: FICA_MATCHING_YES, [FICA_BILLABLE]: BILLABLE },
  [NOT_BILLABLE]: {
    [FICA_OPTION]: FICA_MATCHING_YES,
    [FICA_BILLABLE]: NOT_BILLABLE,
  },
};

export const migrateStdFicaPlans = (coverage) => {
  if (coverage?.name !== STD) return coverage;
  const plans = coverage.details?.[PLANS];
  if (!Array.isArray(plans)) return coverage;

  return {
    ...coverage,
    details: {
      ...coverage.details,
      [PLANS]: plans.map((plan) => {
        const legacy = LEGACY_FICA_PLAN_VALUES[plan?.[FICA_OPTION]];
        return legacy ? { ...plan, ...legacy } : plan;
      }),
    },
  };
};
const PPO_DHMO_UNDERSCORE = PPO_DHMO.replace("/", "_");
const isPpoDhmo = (v) => v === PPO_DHMO || v === PPO_DHMO_UNDERSCORE;

const { CURRENT_COVERAGES, NEW_COVERAGES, SELECTED_COVERAGES } =
  RFP_ACTION_VALUES;

export const getStepLabel = (index) => {
  switch (index) {
    case 0:
      return "RFP Details";
    case 1:
      return "Quote Coverages";
    case 2:
      return "Coverage Details";
    case 3:
      return "Carriers";
    case 4:
      return "Documents";
    case 5:
      return "Review";
    default:
      return "N/A";
  }
};

function formatTimeUnit(value, unit) {
  return `${value} ${unit}${value === 1 ? "" : "s"}`;
}

export const getTimeFromLastUpdate = (lastUpdate) => {
  if (!lastUpdate) return null;

  const delta = Math.abs(Date.now() - new Date(lastUpdate).getTime()) / 1000;
  const days = Math.floor(delta / 86400);
  const hours = Math.floor((delta % 86400) / 3600);
  const minutes = Math.floor((delta % 3600) / 60);
  const seconds = Math.floor(delta % 60);

  if (days > 0) return formatTimeUnit(days, "day");
  if (hours > 0) return formatTimeUnit(hours, "hour");
  if (minutes > 0) return formatTimeUnit(minutes, "minute");
  if (seconds > 0) return formatTimeUnit(seconds, "second");
  return "0 seconds";
};

export const getFilteredCoverages = (rfp) => {
  const filterData = (d) =>
    rfp[SELECTED_COVERAGES]?.filter(({ id }) => d?.some((c) => c.id === id)) ||
    [];
  const currentCoverages = sortProductsByCoverage(
    filterData(rfp[CURRENT_COVERAGES]),
  );
  const newCoverages = sortProductsByCoverage(filterData(rfp[NEW_COVERAGES]));
  const coverages = [...currentCoverages, ...newCoverages];
  return { coverages, currentCoverages, newCoverages };
};

export const validateDueDateWhitinDays = (dueDate) => {
  const { ERROR, WARNING } = DUE_DATE_ALERT_STATUS;
  var dueDateFix = new Date(dueDate);
  dueDateFix.setHours(0, 0, 0, 0);

  const today = new Date();
  today.setHours(0, 0, 0, 0);

  if (dueDateFix < today) {
    return ERROR;
  }

  const sevenDaysFromNow = new Date(today);
  sevenDaysFromNow.setDate(today.getDate() + 7);
  sevenDaysFromNow.setHours(0, 0, 0, 0);

  if (dueDateFix < sevenDaysFromNow) {
    return WARNING;
  }
};

export const buildUploadedDocuments = (uploadedData, newUploadedData) => {
  // Create a map from uploadedData for fast lookup by fileName
  const uploadedDataMap = new Map(
    uploadedData.map((item) => [item.fileName, item]),
  );

  // Process each file in newUploadedData
  newUploadedData.forEach((newFile) => {
    const fileName = newFile.fileName;

    if (uploadedDataMap.has(fileName)) {
      // If the file exists in uploadedData, update the documentTypes
      const existingFile = uploadedDataMap.get(fileName);

      // Retain only documentTypes present in newUploadedData, using values from uploadedData
      Object.keys(newFile.documentTypes || {}).forEach((type) => {
        if (existingFile.documentTypes?.[type]) {
          newFile.documentTypes[type] = existingFile.documentTypes[type]; // Update with uploadedData values
        }
      });

      // Update the uploadedDataMap with the modified newFile
      uploadedDataMap.set(fileName, {
        ...existingFile,
        documentTypes: newFile.documentTypes,
      });
    } else {
      // If the file doesn't exist in uploadedData, append it to the map
      uploadedDataMap.set(fileName, newFile);
    }
  });

  // Return the updated uploadedData array from the map
  return Array.from(uploadedDataMap.values());
};

export const buildDocumentTypes = (
  coverages,
  isCurrentClient,
  documents,
  isDraft,
) => {
  const initDocumentsType = new Map();
  const initUploadedFiles = new Map();
  coverages.forEach((coverage) => {
    const docs = (
      getCoveragesDocuments(isCurrentClient, coverage) || []
    ).filter(Boolean);
    docs.forEach((doc) => {
      const { name, required } = doc;
      const existingDocument = initDocumentsType.get(name);
      let filesCoverages =
        coverage.documents
          ?.filter(({ name }) => name === doc.name)
          ?.map((item) => ({ ...item.file, name: item.fileName })) || [];

      if (isDraft) {
        filesCoverages = filesCoverages.map((fileCoverage) => {
          const fileCoverageName = fileCoverage?.name || fileCoverage?.path;
          if (!fileCoverageName) {
            const document = documents?.find(
              ({ name }) =>
                name === fileCoverage?.path ||
                name?.replace(/\+/g, " ") === fileCoverage?.path,
            );
            if (document) {
              return {
                ...document,
                name: fileCoverage?.path,
              };
            }
          }
          return { ...fileCoverage, name: fileCoverageName };
        });
      }

      const coverageObj = {
        coverageName: coverage.name,
        coverageInfo: coverage,
        files: filesCoverages,
        required,
      };

      filesCoverages.forEach((fileCoverage) => {
        if (fileCoverage) {
          const existingUploadedFile = initUploadedFiles.get(fileCoverage.name);

          if (existingUploadedFile) {
            if (existingUploadedFile.documentTypes[name]) {
              existingUploadedFile.documentTypes[name].push(coverage.name);
            } else {
              existingUploadedFile.documentTypes[name] = [coverage.name];
            }
          } else {
            const fileCoverageName = fileCoverage.name || fileCoverage.path;
            const fileExistInDocuments = documents.find(
              (f) =>
                f.filename === fileCoverageName || f.name === fileCoverageName,
            );
            initUploadedFiles.set(fileCoverage.name || fileCoverage.path, {
              documentTypes: {
                [name]: [coverage.name],
              },
              fileName: fileCoverageName,
              file: {
                ...fileCoverage,
                name: fileCoverageName,
                ...fileExistInDocuments,
              },
            });
          }
        }
      });

      if (existingDocument) {
        existingDocument.coverages[required ? "required" : "optional"].push(
          coverageObj,
        );
      } else {
        initDocumentsType.set(name, {
          documentTypeName: name,
          coverages: {
            required: required ? [coverageObj] : [],
            optional: required ? [] : [coverageObj],
          },
        });
      }
    });
  });

  const initDocumentsTypeObject = Object.fromEntries(initDocumentsType);
  const initUploadedFilesObject = Object.fromEntries(initUploadedFiles);
  const newlyUploadedFiles = {};

  const initiallyUploadedFileNames = new Set(
    Object.keys(initUploadedFilesObject),
  );
  const documentsWithoutCoverage = filterDocuments(
    documents,
    initiallyUploadedFileNames,
  );
  documentsWithoutCoverage?.forEach((item) => {
    const documentName = item.name || item.filename;
    newlyUploadedFiles[documentName] = {
      documentTypes: {},
      fileName: documentName,
      file: {
        ...item,
        name: documentName,
      },
    };

    if (item.types.length) {
      newlyUploadedFiles[documentName]["documentTypes"] = item.types.reduce(
        (acc, typeItem) => ({
          ...acc,
          [typeItem]: [],
        }),
        {},
      );
    }
  });

  documents?.forEach((docFile) => {
    const fileName = docFile.filename;
    const initFile = initUploadedFilesObject[fileName]; // Cache the file object in initFile

    if (initFile) {
      const documentTypes = initFile.documentTypes;
      // Only check for types that are not already in initFile. A freshly
      // uploaded row can still have null types before intake stamps them, so
      // guard it the same way filterDocuments does rather than crash the page.
      docFile.types?.forEach((type) => {
        if (!documentTypes[type]) {
          documentTypes[type] = []; // Append the new document type with an empty array
        }
      });
      initFile.file.types = Object.keys(documentTypes);
      initFile.file = {
        ...initFile.file,
        ...docFile,
      };
    }
  });

  return {
    documentTypes: Object.values(initDocumentsTypeObject),
    uploadedFiles: [
      ...Object.values(initUploadedFilesObject),
      ...Object.values(newlyUploadedFiles),
    ],
  };
};

/**
 * When the EN census is already available, demote the "Census" document type
 * from required to optional so it doesn't block the Documents step.
 */
export const demoteCensusToOptional = (documentTypes) =>
  documentTypes.map((dt) => {
    if (dt.documentTypeName !== "Census") return dt;
    const demotedRequiredCoverages = (dt.coverages.required || []).map(
      (coverage) => ({
        ...coverage,
        required: false,
      }),
    );

    return {
      ...dt,
      employeeNavigatorManaged: true,
      coverages: {
        required: [],
        optional: [
          ...(dt.coverages.optional || []),
          ...demotedRequiredCoverages,
        ],
      },
    };
  });

const filterDocuments = (docs, initiallyUploadedFileNames) =>
  docs?.filter((item) => {
    const documentTypeExist = item.types?.some((docType) =>
      DOCUMENT_ORDER.includes(docType),
    );
    const documentName = item.name || item.filename;
    if (
      documentName &&
      item.types?.length &&
      documentTypeExist &&
      !initiallyUploadedFileNames.has(documentName)
    ) {
      return true;
    }
    return false;
  });

/**
 * Returns the additional-document names the wizard should show: the draft's own
 * list, plus any stored document the checklist won't claim.
 *
 * The checklist is keyed on DOCUMENT_ORDER, so a document whose `types` match
 * none of those names has no place there and belongs here instead. That covers
 * the "Invoice" and "Other" types the RFP intake API assigns — deliberately
 * absent from DOCUMENT_ORDER so they don't become checklist requirements — and
 * the empty `types` on rows written before it assigned them.
 *
 * Reading the rows rather than trusting the draft's list is what makes this
 * reliable: a client that loaded before an upload can no longer drop the
 * document by saving its stale list over the top.
 *
 * Files with no `id` are SharePoint entries with no row behind them. Removing
 * one only drops it from rawForm, so re-deriving it here would resurrect it.
 */
export const mergeAdditionalDocumentNames = (documentNames, rfpFiles) => {
  const names = [...(documentNames ?? [])];
  // SharePoint encodes spaces as "+", so a stored name and the draft's name for
  // the same file can differ — match both ways or the file lands in the list twice.
  const seen = new Set(
    names.flatMap((name) => [name, name.replace(/\+/g, " ")]),
  );

  (rfpFiles ?? []).forEach((file) => {
    if (file?.id == null) {
      return;
    }

    if (file.types?.some((docType) => DOCUMENT_ORDER.includes(docType))) {
      return;
    }

    const fileName = file.filename || file.name;
    if (
      !fileName ||
      seen.has(fileName) ||
      seen.has(fileName.replace(/\+/g, " "))
    ) {
      return;
    }

    seen.add(fileName);
    names.push(fileName);
  });

  return names;
};

/**
 * SharePoint file names are case-insensitive, so "Carrier Email.pdf" and
 * "Carrier email.pdf" are the same file there. Compare taken names
 * lowercased, and free a colliding name with the "_<n>" suffix convention.
 */
export const generateUniqueFileName = (fileName, takenNames) => {
  const taken = new Set([...takenNames].map((name) => name.toLowerCase()));
  if (!taken.has(fileName.toLowerCase())) {
    return fileName;
  }

  const lastDotIndex = fileName.lastIndexOf(".");
  const base = lastDotIndex > 0 ? fileName.slice(0, lastDotIndex) : fileName;
  const ext = lastDotIndex > 0 ? fileName.slice(lastDotIndex) : "";

  let counter = 1;
  let candidate = `${base}_${counter}${ext}`;
  while (taken.has(candidate.toLowerCase())) {
    counter++;
    candidate = `${base}_${counter}${ext}`;
  }
  return candidate;
};

export function getPercentageUploadedDocs(documentTypes, property) {
  const { total, uploaded } = documentTypes.reduce(
    (acc, curr) => {
      const coverages = curr.coverages[property];
      acc.total += coverages.length;
      acc.uploaded += coverages.filter((c) => c.files.length > 0).length;

      return acc;
    },
    {
      total: 0,
      uploaded: 0,
    },
  );
  if (total === 0) {
    return 100;
  }
  if (uploaded === 0) {
    return 0;
  }

  return Math.round((uploaded / total) * 100);
}

export const sortRFPDocumentTypes = (documents) => {
  const censusDocument = documents.find(
    (doc) => doc.documentTypeName === "Census",
  );
  const documentsWithoutCensus = documents.filter(
    (doc) => doc.documentTypeName !== "Census",
  );

  // Split documents into those with required uploads and those with only optional uploads
  const requiredDocuments = [];
  const optionalOnlyDocuments = [];

  documentsWithoutCensus.forEach((doc) => {
    const hasRequiredUploads = doc.coverages.required.length > 0;
    if (hasRequiredUploads) {
      requiredDocuments.push(doc);
    } else {
      optionalOnlyDocuments.push(doc);
    }
  });

  // Apply sort to both required and optional documents
  sortByDocumentOrder(requiredDocuments);
  sortByDocumentOrder(optionalOnlyDocuments);

  // Keep Census pinned at the top even when it becomes optional-only.
  return censusDocument
    ? [censusDocument, ...requiredDocuments, ...optionalOnlyDocuments]
    : [...requiredDocuments, ...optionalOnlyDocuments];
};

// Utility to sort documents based on DOCUMENT_ORDER
const sortByDocumentOrder = (documents) =>
  documents.sort((a, b) => {
    const orderA = DOCUMENT_ORDER.indexOf(a.documentTypeName);
    const orderB = DOCUMENT_ORDER.indexOf(b.documentTypeName);
    return orderA - orderB;
  });

export const sortProductsByCoverage = (products, keyName = "name") => {
  if (!products?.length) return products;

  return products.sort((a, b) => {
    const valueA = typeof a === "object" ? a[keyName] || a.product?.name : a;
    const valueB = typeof b === "object" ? b[keyName] || b.product?.name : b;

    const indexA = COVERAGE_ORDER.indexOf(valueA);
    const indexB = COVERAGE_ORDER.indexOf(valueB);

    if (indexA === -1 && indexB === -1) return 0;
    if (indexA === -1) return 1;
    if (indexB === -1) return -1;

    return indexA - indexB;
  });
};

const getDisplayName = (productType, planType = "") => {
  const mapping = productDisplayNameMap[productType];
  if (typeof mapping === "string") return mapping;
  if (typeof mapping === "object" && planType)
    return mapping[planType] || productType;
  return productType;
};

export const sortProductsByQuoteProductType = (products) => {
  if (!products?.length) return products;
  const productsWithDisplayName = products.map((item) => ({
    ...item,
    displayName: getDisplayName(item.productType, item.productPlanType),
  }));
  return sortProductsByCoverage(productsWithDisplayName, "displayName");
};

function makeDhmoPlans(plans) {
  const mapped =
    plans?.map((p) => ({ ...p, name: null, displayName: NO_PLAN })) ?? [];
  return mapped.length ? mapped : [{ name: null, displayName: NO_PLAN }];
}

function makePpoPlans(plans, planType) {
  const mapped = plans?.map((p) => ({ ...p, name: planType })) ?? [];
  return mapped.length ? mapped : [{ name: planType }];
}

function makeDualPpoPlans(plans) {
  const arr = plans ?? [];
  return [
    { ...(arr[0] || {}), name: DUAL_OPTION_PPO_1 },
    { ...(arr[1] || {}), name: DUAL_OPTION_PPO_2 },
  ];
}

function splitDualPpoDhmo(cov, byName) {
  const plansPPO = (cov.details[PLANS] || []).filter((p) =>
    /PPO/i.test(p.name),
  );
  const plansDHMO = (cov.details[PLANS] || []).filter((p) =>
    /DHMO/i.test(p.name),
  );

  const ppoProd = byName(DENTAL_PPO);
  const hmoProd = byName(DENTAL_DHMO);

  return [
    {
      ...cov,
      ...ppoProd,
      id: ppoProd.id,
      name: DENTAL_PPO,
      details: {
        ...cov.details,
        [PLAN_TYPE]: SINGLE_OPTION_PPO,
        [DUAL_PLAN_TYPE]: null,
        [PLANS]: makePpoPlans(plansPPO, SINGLE_OPTION_PPO),
      },
    },
    {
      ...cov,
      ...hmoProd,
      id: hmoProd.id,
      name: DENTAL_DHMO,
      details: {
        ...cov.details,
        [PLAN_TYPE]: null,
        [DUAL_PLAN_TYPE]: null,
        [PLANS]: makeDhmoPlans(plansDHMO),
      },
    },
  ];
}

function normalizeSingleDental(cov, byName) {
  const currentPlanType = cov.details?.[PLAN_TYPE];
  const isHmo = currentPlanType === DHMO;
  const isDualPPO =
    currentPlanType === DUAL_OPTION_PPO ||
    (currentPlanType === DUAL_OPTION &&
      !isPpoDhmo(cov.details?.[DUAL_PLAN_TYPE]));
  const target = isHmo ? DENTAL_DHMO : DENTAL_PPO;
  const prod = byName(target);

  const patched = { ...cov.details };
  if (isHmo) {
    patched[PLAN_TYPE] = null;
    patched[PLANS] = makeDhmoPlans(patched[PLANS]);
  } else if (isDualPPO) {
    patched[PLAN_TYPE] = DUAL_OPTION_PPO;
    patched[DUAL_PLAN_TYPE] = PPO1_PPO2;
    patched[PLANS] = makeDualPpoPlans(patched[PLANS]);
  } else {
    patched[PLAN_TYPE] = SINGLE_OPTION_PPO;
    patched[PLANS] = makePpoPlans(patched[PLANS], SINGLE_OPTION_PPO);
  }
  return [{ ...cov, ...prod, id: prod.id, name: target, details: patched }];
}

export function normalizeDentalCoverages(selected = [], productCatalog = []) {
  const byName = (name) => productCatalog.find((p) => p.name === name) || {};

  return selected.flatMap((cov) => {
    if (cov.name === DENTAL_PPO || cov.name === DENTAL_DHMO) return cov;

    if (
      cov.name === DENTAL &&
      cov.details?.[PLAN_TYPE] === DUAL_OPTION &&
      isPpoDhmo(cov.details?.[DUAL_PLAN_TYPE])
    ) {
      return splitDualPpoDhmo(cov, byName);
    }

    if (cov.name === DENTAL) {
      return normalizeSingleDental(cov, byName);
    }

    return cov;
  });
}
